import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, AreaChart, Area } from 'recharts';
import axios from 'axios';
import moment from 'moment';
import { ArrowLeftIcon, ArrowRightIcon, CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';

const DashboardChart = React.memo(({ packId }) => {
  const [chartData, setChartData] = useState([]);
  const [subjects, setSubjects] = useState({});
  const [selectedSubject, setSelectedSubject] = useState('overall');
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 10 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showChart, setShowChart] = useState(true);
  const [meta, setMeta] = useState(null);
  const [activeDotInfo, setActiveDotInfo] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const touchStartX = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`/api/packs/${packId}/common-score-growth-chart`);
        if (response?.data?.success) {
          setShowChart(response.data.data.showChart);
          setChartData(response.data.data.chartData || []);
          setSubjects(response.data.data.subjects || {});
          setMeta(response.data.data.commonScoreGrowthChartMeta);
        }
      } catch (error) {}
    };
    fetchData();
  }, [packId]);

  const itemsPerView = useMemo(() => {
    if (isMobile) return 3;
    if (isTablet) return 6;
    return 15;
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (!chartData.length) return;

    if (chartData.length <= itemsPerView) {
      setVisibleRange({ start: 0, end: chartData.length });
    } else {
      setVisibleRange({
        start: Math.max(0, chartData.length - itemsPerView),
        end: chartData.length,
      });
    }
  }, [chartData, itemsPerView]);

  const handleScroll = useCallback(
    (direction) => {
      if (chartData.length <= itemsPerView) return;

      const total = chartData.length;

      setVisibleRange((prev) => {
        if (direction === 'left') {
          return {
            start: Math.max(0, prev.start - 1),
            end: Math.max(itemsPerView, prev.end - 1),
          };
        }

        return {
          start: Math.min(total - itemsPerView, prev.start + 1),
          end: Math.min(total, prev.end + 1),
        };
      });
    },
    [chartData.length, itemsPerView],
  );

  const handleWheel = useCallback(
    (e) => {
      if (chartData.length <= itemsPerView) return;

      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        handleScroll(e.deltaX > 0 ? 'right' : 'left');
      }
    },
    [handleScroll, chartData.length, itemsPerView],
  );

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = useCallback(
    (e) => {
      if (chartData.length <= itemsPerView) return;
      if (!touchStartX.current) return;

      const diff = e.touches[0].clientX - touchStartX.current;

      if (Math.abs(diff) > 20) {
        handleScroll(diff > 0 ? 'left' : 'right');
        touchStartX.current = null;
      }
    },
    [handleScroll, chartData.length, itemsPerView],
  );

  const CustomTooltip = useCallback(
    ({ active, payload }) => {
      if (active && payload && payload.length && activeDotInfo) {
        const data = payload[0].payload;
        const bgColor = data.fullData.color;
        const formatPercentile = (p, title) => {
          if (p === undefined || p === null) return '';

          // If numeric → format properly
          if (!isNaN(Number(p))) {
            const val = Math.round(Number(p) * 100) / 100;
            return title ? `${title} ${val}%ile` : `${val}%ile`;
          }

          // If string like "99.9+" → return directly
          return title ? `${title} ${p}%ile` : `${p}%ile`;
        };

        return (
          <div style={{ position: 'absolute', pointerEvents: 'none', zIndex: 20 }}>
            <div
              style={{
                position: 'absolute',
                left: activeDotInfo.cx - 1,
                top: activeDotInfo.cy - 80,
                width: 2,
                height: 80,
                borderLeft: '2px dotted ' + bgColor,
              }}
            />

            <div
              style={{
                position: 'absolute',
                left: activeDotInfo.cx - 90,
                top: activeDotInfo.cy - 140,
                background: bgColor,
                color: 'var(--q3-neutral-default_inverted)',
                padding: '5px 5px',
                borderRadius: 12,
                minWidth: 180,
                textAlign: 'center',
                fontFamily: 'Inter',
              }}
            >
              <div className="font-bold text-sm mb-1">{data.title}</div>

              {selectedSubject === 'overall' ? (
                <>
                  <div className="text-sm">
                    Score:{' '}
                    <span className="font-semibold">
                      {data.fullData.totalMarkScored}/{data.testTotalScore}
                    </span>
                  </div>

                  {data.percentile?.data && (
                    <div className="text-sm">
                      Predicted:{' '}
                      <span className="font-semibold">
                        {formatPercentile(data.percentile?.data, data.percentile?.title)}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm">
                  Score:{' '}
                  <span className="font-semibold">
                    {
                      data.fullData.subjectsMarkScored?.find((s) => s.subjectId._id === selectedSubject)
                        ?.totalMarkScored
                    }
                    /{data.fullData.subjectsTotalScore?.find((s) => s.subjectId === selectedSubject)?.totalMarks}
                  </span>
                </div>
              )}

              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderTop: `10px solid ${bgColor}`,
                  margin: '0 auto',
                  marginTop: 6,
                }}
              />
            </div>
          </div>
        );
      }

      return null;
    },
    [activeDotInfo, selectedSubject],
  );
  const CustomDot = useCallback(({ cx, cy, payload }) => {
    const bgColor = payload.color;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={bgColor === 'var(--q3-accent-normal)' ? 5 : 6}
        fill={bgColor}
        stroke="white"
        strokeWidth={2}
      />
    );
  }, []);

  const handleActiveDot = useCallback((cx, cy, bgColor) => {
    setActiveDotInfo((prev) => {
      if (prev?.cx === cx && prev?.cy === cy) return prev;
      return { cx, cy, bgColor };
    });
  }, []);

  const CustomActiveDot = ({ cx, cy, payload }) => {
    const bgColor = payload.color;
    handleActiveDot(cx, cy, bgColor);
    return <circle cx={cx} cy={cy} r={7} fill={bgColor} stroke="white" strokeWidth={2} />;
  };

  // Generate mock data for null state
  const generateMockData = useCallback(() => {
    const mockData = [];
    for (let i = 0; i < 10; i++) {
      mockData.push({
        name: `Test ${i + 1}`,
        title: `Mock Test ${i + 1}`,
        shortTitle: `Test ${i + 1}`,
        color: '#888888',
        score: Math.floor(Math.random() * 250) + 50,
        testTotalScore: 300,
        fullData: {
          color: '#888888',
          totalMarkScored: Math.floor(Math.random() * 250) + 50,
        },
      });
    }
    return mockData;
  }, []);

  const data = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];

    const visibleData = chartData.slice(visibleRange.start, visibleRange.end);

    return visibleData.map((test, index) => {
      const actualIndex = visibleRange.start + index;

      if (selectedSubject === 'overall') {
        return {
          name: `Test ${actualIndex + 1}`,
          title: test.title?.split(' - Updated as per Latest NTA Pattern')[0],
          color: test.color,
          shortTitle: test.shortTitle,
          score: Math.max(0, test.totalMarkScored),
          submittedAt: moment(test.submittedAt).format("DD MMM'YY"),
          percentile: test.percentile || '',
          testTotalScore: test.testTotalScore,
          fullData: test,
        };
      } else {
        const subjectData = test.subjectsMarkScored.find((s) => s.subjectId._id === selectedSubject);
        const subjectTotal = test.subjectsTotalScore.find((s) => s.subjectId === selectedSubject);

        return {
          name: `Test ${actualIndex + 1}`,
          title: test.title,
          shortTitle: test.shortTitle,
          submittedAt: moment(test.submittedAt).format("DD MMM'YY"),
          color: test.color,
          score: Math.max(0, subjectData?.totalMarkScored || 0),
          totalMarks: subjectTotal?.totalMarks || 100,
          fullData: test,
        };
      }
    });
  }, [chartData, visibleRange, selectedSubject]);

  if (!meta) return null;

  if (!showChart) {
    const mockData = generateMockData();
    const overallTicks = [0, 60, 120, 180, 240, 300];
    const yAxisTicks = overallTicks;

    return (
      <div className="w-full px-0 md:px-2 py-4 md:py-6">
        <div
          className={` ${!expanded ? 'p-4' : 'p-4 md:p-6'} bg-[var(--q3-surface-default)] rounded-[12px] border border-[var(--q3-stroke-light)] relative`}
        >
          <div className={`flex justify-between ${!expanded ? 'mb-0 items-center' : 'mb-6 items-start'}`}>
            <div className="w-full">
              <h2
                className={`text-[16px] md:text-[18px] font-inter font-bold leading-normal text-[var(--q3-neutral-default)] ${!expanded ? 'mb-0' : 'mb-2'}`}
              >
                {meta?.title || 'Score and Predicted Percentile for QFTs & PYQs'}
              </h2>

              {expanded && (
                <>
                  <p
                    className="text-[11px] md:text-[12px] font-inter font-normal leading-normal"
                    style={{ color: 'var(--q3-neutral-light)' }}
                  >
                    {meta?.description || 'This graph shows how you performed in the tests'}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {[
                      { id: 'overall', title: 'Overall', shortName: 'Overall' },
                      { id: 'phy', title: 'Physics', shortName: 'Phy' },
                      { id: 'chem', title: 'Chemistry', shortName: 'Chem' },
                      { id: 'math', title: 'Maths', shortName: 'Math' },
                    ].map((subject) => (
                      <button
                        key={subject.id}
                        className="px-3 py-2 md:px-2 md:py-1 rounded-[var(--radius-3xl,360px)] text-[12px] md:text-[14px] font-medium bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] border border-[var(--q3-stroke-normal)] cursor-not-allowed "
                      >
                        {subject.shortName}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-2">
              {/* <button
                onClick={() => navigate(`/v3/${packId}/timelineview`)}
                className="py-2 px-4 rounded-[360px] border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-glass-normal)] cursor-pointer hover:bg-[var(--q3-surface-dim)] text-[var(--q3-accent-normal)] whitespace-nowrap"
              >
                Timeline View
              </button> */}
              <button
                disabled
                className="p-2 rounded-[6.667px] border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-gray-300 cursor-not-allowed hover:bg-[var(--q3-surface-dim)] hidden md:block"
              >
                <ArrowLeftIcon className="w-6 h-4" />
              </button>

              <button
                disabled
                className="p-2 rounded-[6.667px] border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-gray-300 cursor-not-allowed hover:bg-[var(--q3-surface-dim)] hidden md:block"
              >
                <ArrowRightIcon className="w-6 h-4" />
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded((prev) => !prev)}
                className="rounded-full border p-2 text-[var(--q3-accent-normal)]"
              >
                {expanded ? 'HIDE' : 'VIEW'}
                {expanded ? <CaretUpIcon className="h-4 w-4 ml-1" /> : <CaretDownIcon className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>

          {expanded && (
            <div className="relative" style={{ minHeight: 400 }}>
              <div
                className="absolute inset-0 w-full h-full blur-[6px] opacity-60 pointer-events-none"
                style={{ filter: 'blur(6px) saturate(0.8)' }}
              >
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={mockData} margin={{ top: 20, right: 0, left: 0, bottom: 40 }}>
                    <defs>
                      <linearGradient id="areaFillMock" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(181,183,192,0.26)" />
                        <stop offset="100%" stopColor="rgba(181,183,192,0)" />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" vertical={false} />

                    <XAxis
                      dataKey="shortTitle"
                      tick={{ fill: 'rgba(0,0,0,0.3)', fontSize: 10 }}
                      height={10}
                      interval={0}
                    />

                    <YAxis
                      ticks={yAxisTicks}
                      domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                      tick={{ fill: 'rgba(0,0,0,0.3)', fontSize: 12 }}
                    />

                    <ReferenceLine y={0} stroke="rgba(0,0,0,0.1)" strokeDasharray="3 3" />

                    <Area
                      type="linear"
                      dataKey="score"
                      stroke="rgba(0,0,0,0.2)"
                      strokeWidth={2}
                      fill="url(#areaFillMock)"
                      dot={({ cx, cy, index }) => (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={4}
                          fill={index % 2 === 0 ? 'var(--q3-danger-normal)' : 'var(--q3-accent-normal)'}
                          stroke="white"
                          strokeWidth={1.2}
                        />
                      )}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="text-center max-w-lg mx-auto px-4">
                  <p className="text-[14px] md:text-[16px] font-inter font-medium text-[var(--q3-neutral-default)] leading-tight  backdrop-blur-sm py-4 px-2 rounded-lg">
                    {meta?.fallbackMessage || 'Attempt at least 3 tests to unlock and view your Score Growth Graph.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const overallTicks = [0, 60, 120, 180, 240, 300];
  const subjectTicks = [0, 20, 40, 60, 80, 100];
  const yAxisTicks = selectedSubject === 'overall' ? overallTicks : subjectTicks;

  // Check if arrows should be disabled
  const isLeftDisabled = visibleRange.start === 0;
  const isRightDisabled = visibleRange.end >= chartData.length;

  return (
    <div className="w-full px-0 md:px-2 py-4 md:py-6">
      <div className="p-4 md:p-6 bg-[var(--q3-surface-default)] rounded-[12px] border border-[var(--q3-stroke-light)] relative flex flex-col">
        <div className="flex justify-between items-start mb-4 gap-1.5">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-inter font-bold leading-normal text-[var(--q3-neutral-default)] mb-2 ">
              {meta?.title || 'Score and Predicted Percentile for QFTs & PYQs'}
            </h2>

            <p
              className="text-[11px] md:text-[12px] font-inter font-normal leading-normal"
              style={{ color: 'var(--q3-neutral-light)' }}
            >
              {meta?.description || 'This graph shows how you performed in the tests'}
            </p>
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-2">
            {/* <button
              onClick={() => navigate(`/v3/${packId}/timelineview`)}
              className="py-2 px-2 md:px-4 text-sm md:text-base rounded-[360px] border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-glass-normal)] cursor-pointer hover:bg-[var(--q3-surface-dim)] text-[var(--q3-accent-normal)] whitespace-nowrap"
            >
              Timeline View
            </button> */}
            <button
              onClick={() => handleScroll('left')}
              disabled={isLeftDisabled}
              className={`p-2 rounded-[6.667px] border border-[var(--q3-stroke-normal)]
              bg-[var(--q3-surface-default)] transition-all hidden md:block
              ${isLeftDisabled ? 'text-[var(--q3-neutral-light)] cursor-not-allowed' : 'text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dim)] cursor-pointer'}`}
            >
              <ArrowLeftIcon
                className="w-6 h-4"
                color={isLeftDisabled ? 'var(--q3-neutral-light)' : 'var(--q3-neutral-default)'}
              />
            </button>

            <button
              onClick={() => handleScroll('right')}
              disabled={isRightDisabled}
              className={`p-2 rounded-[6.667px] border border-[var(--q3-stroke-normal)]
              bg-[var(--q3-surface-default)] transition-all hidden md:block
              ${isRightDisabled ? 'text-[var(--q3-neutral-light)] cursor-not-allowed' : 'text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dim)] cursor-pointer'}`}
            >
              <ArrowRightIcon
                className="w-6 h-4"
                color={isRightDisabled ? 'var(--q3-neutral-light)' : 'var(--q3-neutral-default)'}
              />
            </button>
          </div>
        </div>

        <div className="flex flex-nowrap md:flex-wrap gap-2 overflow-x-auto md:overflow-visible mb-4 md:mb-6">
          {Object.entries(subjects || {}).map(([id, subject]) => (
            <button
              key={id}
              onClick={() => setSelectedSubject(id)}
              className={`
        px-3 py-2
        md:px-3.5 md:py-2       /* Increased size on tablet */
        lg:px-4 lg:py-2
        rounded-[var(--radius-3xl,360px)]
        text-[12px]
        md:text-[13px]          /* Bigger text on tablet */
        lg:text-sm
        font-medium transition-all whitespace-nowrap
        ${
          selectedSubject === id
            ? `bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]`
            : `bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] border border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dim)] cursor-pointer`
        }
      `}
            >
              <span className="flex items-center gap-1 md:gap-2 lg:gap-1.5">
                <img src={subject.icon} alt={subject.title} className="w-3 h-3 md:w-3 md:h-3 lg:w-4 lg:h-4" />
                {subject.shortName || subject.title}
              </span>
            </button>
          ))}
        </div>

        {/* Interactive Chart Area */}
        <div onWheel={handleWheel} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} className="relative">
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 40 }}>
              <defs>
                <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(181,183,192,0.26)" />
                  <stop offset="100%" stopColor="rgba(181,183,192,0)" />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} opacity={0.8} />

              <XAxis dataKey="shortTitle" tick={{ fill: '#999', fontSize: 10 }} height={10} interval={0} />

              <YAxis
                ticks={yAxisTicks}
                domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                tick={{ fill: '#999', fontSize: 12 }}
              />

              <Tooltip content={<CustomTooltip />} cursor={false} />

              <ReferenceLine y={0} stroke="#ccc" strokeDasharray="3 3" />

              <Area
                type="linear"
                dataKey="score"
                stroke="#ccc"
                strokeWidth={2}
                fill="url(#areaFill)"
                dot={CustomDot}
                activeDot={CustomActiveDot}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
});

export default DashboardChart;
